/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import Invoiceedit from "../../../assets/images/invoice-edit.svg";
import Invoicenotes from "../../../assets/images/invoice-notes.svg";
import InvoicenotesCompleted from "../../../assets/images/notes-tick.svg";
import React from "react";
import { Tooltip, notification } from "antd";
import { Collapse, Modal, Spin, Dropdown, Button, Alert, Switch, AutoComplete } from "antd";
import {
  DownloadOutlined,
  MoreOutlined,
  RollbackOutlined,
  UserSwitchOutlined,
  WarningOutlined,
} from "@ant-design/icons";
import drag from "../../../assets/images/drag.svg";
import trash from "../../../assets/images/delete.svg";
import downarrow from "../../../assets/images/black-down-arrow.svg";
import uparrow from "../../../assets/images/black-up-arrow.svg";
import backarrow from "../../../assets/images/backarrow.svg";
import addTreatmentIcon from "../../../assets/images/add-treatment.svg";
import { Input } from "antd";
import DocumentViewer from "./DocumentViewer";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import { Reorder, getItemStyle, handleDownload } from "./utils";
import LineItem from "./LineItem";
import VatBreakdown from "./VatBreakdown";
import {
  computeQuantityAwareTotal,
  getLineTotal,
  formatMoney,
} from "./totalsUtil";
import { buildPreviewHitTargets } from "./documentHighlightUtil";
import { v4 as uuid } from "uuid";
import { useTranslation } from "react-i18next";
import CustomModal from "views/components/common/custommodal/CustomModal";
import MoneyInput from "views/components/common/Input/MoneyInput";
import DateField from "views/components/common/Input/DateField";
import NotesModal from "views/components/common/notes/NotesModal";
import apiClient from "utilities/apiClient";
import documentService from "../Extractpdf/services/documentService";
import environment from "environment";
import { translateInvoiceLanguage } from "utilities/TranslationUtility";
import { useSelector } from "react-redux";
import {
  STATIC_CLASSIFICATION_METADATA,
  buildClassificationFeedbackSubmissions,
  collectClassificationChanges,
  getFeedbackSignature,
  getInvoiceClassificationValidationErrors,
  hasPendingClassificationChange,
  markClassificationBaselineSaved,
  normalizeClassificationMetadata,
  prepareClassificationItem,
  sanitizeDuplicatedClassificationItem,
} from "./classificationFeedback";

const normalizePreviewQuery = (value) =>
  String(value || "")
    .normalize("NFKC")
    .toLocaleLowerCase()
    .replace(/[^\p{L}\p{N}]+/gu, "");

const createLineItemWithFeedbackSnapshot = (item = {}) =>
  prepareClassificationItem(item, uuid);

const prepareInvoiceForEditor = (invoiceDetails = {}) => ({
  ...invoiceDetails,
  extracted_invoice_data: (invoiceDetails?.extracted_invoice_data || []).map(
    (pageData) => ({
      ...pageData,
      treatments: (pageData?.treatments || []).map((treatment) => ({
        ...treatment,
        key: treatment?.key || uuid(),
        Items: (treatment?.Items || []).map(createLineItemWithFeedbackSnapshot),
      })),
    })
  ),
});

const cloneInvoiceData = (invoiceDetails) =>
  invoiceDetails ? JSON.parse(JSON.stringify(invoiceDetails)) : invoiceDetails;

const prepareInvoiceUpdatePayload = (invoiceDetails = {}) => ({
  ...invoiceDetails,
  extracted_invoice_data: (invoiceDetails?.extracted_invoice_data || []).map(
    (pageData) => ({
      ...pageData,
      treatments: (pageData?.treatments || []).map((treatment) => ({
        ...treatment,
        Items: (treatment?.Items || []).map((item) => ({ ...item })),
      })),
    })
  ),
});

const markInvoiceClassificationBaselinesSaved = (invoiceDetails = {}) => ({
  ...invoiceDetails,
  extracted_invoice_data: (invoiceDetails?.extracted_invoice_data || []).map(
    (pageData) => ({
      ...pageData,
      treatments: (pageData?.treatments || []).map((treatment) => ({
        ...treatment,
        Items: (treatment?.Items || []).map(markClassificationBaselineSaved),
      })),
    })
  ),
});


const Invoice = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const currentLanguage = useSelector((state) => state.common.currentLanguage);
  const enableItemClassification = environment.enableItemClassification;
  const enableQuantityTotals = environment.enableQuantityTotals;
  const [extractionId, setExtractionId] = useState(null);
  const [initialInvoiceData, setInitialInvoiceData] = useState(null);
  const [invoiceData, setInvoiceData] = useState(null);
  const [selectedPage, setSelectedPage] = useState(1);
  const [images, setImages] = useState(null);
  const [loading, setLoading] = useState(false);
  const [translatingReasonings, setTranslatingReasonings] = useState(false);
  const [confirmationPopup, setConfirmationPopup] = useState({
    isShow: false,
    type: null,
    treatmentKey: null,
    itemKey: null,
    assigned_to: null,
  });
  const [isNotesModalVisible, setIsNotesModalVisible] = useState(false);
  const [notes, setNotes] = useState(null);
  const [documentId, setDocumentId] = useState(null);
  const [sumValues, setSumValues] = useState(null);
  const [isSumManual, setSumManual] = useState(false);
  // R14: per-field override switches replacing the single shared isSumManual flag
  // (only used when enableQuantityTotals is on). Saved payload keeps `is_manual`.
  const [manualOverrides, setManualOverrides] = useState({
    calculated_total_sum: false,
    calculated_total_sum_after_discount: false,
  });
  const [isOwner, setIsOwner] = useState(false);
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState(null);
  const loadedRef = useRef(false);
  const suppressDirtyRef = useRef(false);
  const draftTimerRef = useRef(null);
  const keyActionsRef = useRef({});
  const highlightClearTimerRef = useRef(null);
  const [previewHighlight, setPreviewHighlight] = useState(null);
  const [activePreviewField, setActivePreviewField] = useState(null);
  const [openTreatmentKeys, setOpenTreatmentKeys] = useState([]);
  const [totalsCollapseOpen, setTotalsCollapseOpen] = useState(false);
  const previewJumpTimerRef = useRef(null);
  const stickyBarRef = useRef(null);
  // R16: attention workflow — counter + Alt+N jump between fields needing review.
  const invoiceFormRef = useRef(null);
  const attentionIndexRef = useRef(-1);
  const [attentionCount, setAttentionCount] = useState(0);
  // R20: duplicate-invoice warning, autocomplete options, PLZ validation.
  const [duplicateInfo, setDuplicateInfo] = useState(null);
  const [checkingDuplicate, setCheckingDuplicate] = useState(false);
  const [vetOptions, setVetOptions] = useState([]);
  const [policyholderOptions, setPolicyholderOptions] = useState([]);
  const [plzErrors, setPlzErrors] = useState({});
  const [classificationMetadata, setClassificationMetadata] = useState(
    STATIC_CLASSIFICATION_METADATA
  );
  const [classificationErrors, setClassificationErrors] = useState({});
  const [feedbackStatuses, setFeedbackStatuses] = useState({});
  const feedbackIdempotencyKeysRef = useRef(new Map());
  const acknowledgedFeedbackRef = useRef(new Set());

  const draftStorageKey = (docId) => `invoice_draft_${docId}`;
  const feedbackRetryStorageKey = (docId) =>
    `classification_feedback_retry_${docId}`;
  const persistFeedbackRetryState = (docId) => {
    try {
      sessionStorage.setItem(
        feedbackRetryStorageKey(docId),
        JSON.stringify({
          keys: Array.from(feedbackIdempotencyKeysRef.current.entries()),
          acknowledged: Array.from(acknowledgedFeedbackRef.current),
        })
      );
    } catch (storageError) {
      // In-memory retry protection remains available when storage is blocked.
    }
  };

  useEffect(() => {
    let cancelled = false;
    apiClient
      .get("/classification/metadata")
      .then((response) => {
        if (!cancelled) {
          setClassificationMetadata(normalizeClassificationMetadata(response));
        }
      })
      .catch(() => {
        if (!cancelled) {
          setClassificationMetadata(STATIC_CLASSIFICATION_METADATA);
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const currentPageData =
    invoiceData?.extracted_invoice_data?.[selectedPage - 1] || null;
  const previewHitTargets = useMemo(
    () =>
      buildPreviewHitTargets({
        treatments: currentPageData?.treatments || [],
        sumValues,
        pageNo: selectedPage,
      }),
    [currentPageData?.treatments, sumValues, selectedPage]
  );

  const focusCorrectionField = (target) => {
    if (!target?.field) return;
    const isSumField =
      !target.itemKey &&
      [
        "invoice_total_sum_price_without_vat",
        "invoice_total_sum_price_with_vat",
        "total_discount_amount",
        "calculated_total_sum_manual",
        "calculated_total_sum_after_discount_manual",
      ].includes(target.field);

    if (target.treatmentKey) {
      setOpenTreatmentKeys((prev) =>
        prev.includes(String(target.treatmentKey))
          ? prev
          : [...prev, String(target.treatmentKey)]
      );
    }
    if (isSumField) {
      setTotalsCollapseOpen(true);
    }

    setActivePreviewField({
      itemKey: target.itemKey || null,
      treatmentKey: target.treatmentKey || null,
      field: target.field,
    });

    if (previewJumpTimerRef.current) {
      clearTimeout(previewJumpTimerRef.current);
    }
    previewJumpTimerRef.current = setTimeout(() => {
      const container = invoiceFormRef.current;
      if (!container) return;
      let selector = `[data-preview-field="${target.field}"]`;
      if (target.itemKey) {
        selector += `[data-preview-item-key="${target.itemKey}"]`;
      } else if (target.treatmentKey) {
        selector += `[data-preview-treatment-key="${target.treatmentKey}"]`;
      }
      const el = container.querySelector(selector);
      if (!el) return;
      container
        .querySelectorAll(".attention-focus-ring")
        .forEach((node) => node.classList.remove("attention-focus-ring"));
      el.classList.add("attention-focus-ring");
      el.scrollIntoView({ behavior: "smooth", block: "center" });
      const focusable = el.matches("input, textarea, select, [tabindex]")
        ? el
        : el.querySelector(
            "input, textarea, select, .ant-select-selector, [tabindex]"
          );
      if (focusable?.focus) {
        focusable.focus({ preventScroll: true });
        if (typeof focusable.select === "function") {
          try {
            focusable.select();
          } catch (_) {
            // some inputs may reject select()
          }
        }
      }
      previewJumpTimerRef.current = null;
    }, 160);
  };

  const applyPreviewTargetHighlight = (target) => {
    if (!target) {
      setPreviewHighlight(null);
      setActivePreviewField(null);
      return;
    }
    const currentPage =
      invoiceData?.extracted_invoice_data?.[selectedPage - 1];
    const pageItems = (currentPage?.treatments || []).flatMap(
      (treatment) => treatment?.Items || []
    );
    const item = target.itemKey
      ? pageItems.find((candidate) => candidate?.key === target.itemKey)
      : null;
    const query = String(item?.["Item Name"] || target.query || "").trim();
    const normalizedQuery = normalizePreviewQuery(query);
    const itemIndex = item
      ? pageItems.findIndex((candidate) => candidate?.key === item.key)
      : -1;
    const occurrence =
      itemIndex >= 0
        ? pageItems
            .slice(0, itemIndex)
            .filter(
              (candidate) =>
                normalizedQuery &&
                normalizePreviewQuery(candidate?.["Item Name"]) ===
                  normalizedQuery
            ).length
        : 0;

    setPreviewHighlight({
      itemKey: target.itemKey || null,
      treatmentKey: target.treatmentKey || null,
      field: target.field,
      pageNo: target.pageNo || selectedPage,
      query,
      occurrence,
      sourceBbox: target.sourceBbox || item?.source_bbox || null,
    });
    setActivePreviewField({
      itemKey: target.itemKey || null,
      treatmentKey: target.treatmentKey || null,
      field: target.field,
    });
  };

  const handlePreviewTargetHover = (target) => {
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
      highlightClearTimerRef.current = null;
    }
    if (!target) {
      highlightClearTimerRef.current = setTimeout(() => {
        setPreviewHighlight(null);
        setActivePreviewField(null);
        highlightClearTimerRef.current = null;
      }, 140);
      return;
    }
    applyPreviewTargetHighlight(target);
  };

  const handlePreviewTargetSelect = (target) => {
    if (!target) return;
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
      highlightClearTimerRef.current = null;
    }
    applyPreviewTargetHighlight(target);
    focusCorrectionField(target);
  };

  const handleItemHighlightStart = (item, field = "Item Name") => {
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
      highlightClearTimerRef.current = null;
    }
    const query = String(item?.["Item Name"] || "").trim();
    const normalizedQuery = normalizePreviewQuery(query);
    let occurrence = 0;
    let foundItem = false;
    const currentPage =
      invoiceData?.extracted_invoice_data?.[selectedPage - 1];
    for (const treatment of currentPage?.treatments || []) {
      for (const candidate of treatment?.Items || []) {
        if (candidate?.key === item?.key) {
          foundItem = true;
          break;
        }
        if (
          normalizedQuery &&
          normalizePreviewQuery(candidate?.["Item Name"]) === normalizedQuery
        ) {
          occurrence += 1;
        }
      }
      if (foundItem) break;
    }

    const fieldTarget = previewHitTargets.find(
      (target) => target.itemKey === item?.key && target.field === field
    );
    const nextBbox = fieldTarget?.sourceBbox || item?.source_bbox || null;

    setPreviewHighlight((current) => {
      if (
        current?.itemKey === item?.key &&
        current?.field === field &&
        current?.sourceBbox === nextBbox
      ) {
        return current;
      }
      return {
        itemKey: item?.key,
        treatmentKey: fieldTarget?.treatmentKey || null,
        field,
        pageNo: selectedPage,
        query,
        occurrence,
        sourceBbox: nextBbox,
      };
    });
    setActivePreviewField({
      itemKey: item?.key || null,
      treatmentKey: fieldTarget?.treatmentKey || null,
      field,
    });
  };

  const handleItemHighlightEnd = (itemKey) => {
    if (highlightClearTimerRef.current) {
      clearTimeout(highlightClearTimerRef.current);
    }
    highlightClearTimerRef.current = setTimeout(() => {
      setPreviewHighlight((current) =>
        current?.itemKey === itemKey ? null : current
      );
      setActivePreviewField((current) =>
        current?.itemKey === itemKey ? null : current
      );
      highlightClearTimerRef.current = null;
    }, 140);
  };

  useEffect(
    () => () => {
      if (highlightClearTimerRef.current) {
        clearTimeout(highlightClearTimerRef.current);
      }
      if (previewJumpTimerRef.current) {
        clearTimeout(previewJumpTimerRef.current);
      }
    },
    []
  );

  // Keep form/preview clearance equal to the real sticky-bar height (wraps on small screens).
  useEffect(() => {
    const el = stickyBarRef.current;
    if (!el || typeof ResizeObserver === "undefined") return undefined;
    const publish = () => {
      document.documentElement.style.setProperty(
        "--invoice-sticky-bar-height",
        `${Math.ceil(el.getBoundingClientRect().height)}px`
      );
    };
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    window.addEventListener("resize", publish);
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", publish);
      document.documentElement.style.removeProperty("--invoice-sticky-bar-height");
    };
  }, [attentionCount, enableItemClassification, isOwner, loading, translatingReasonings]);

  useEffect(() => {
    if (!previewHighlight?.itemKey) return;
    const currentPage =
      invoiceData?.extracted_invoice_data?.[selectedPage - 1];
    const pageItems = (currentPage?.treatments || []).flatMap(
      (treatment) => treatment?.Items || []
    );
    const activeIndex = pageItems.findIndex(
      (item) => item?.key === previewHighlight.itemKey
    );
    const activeItem = pageItems[activeIndex];
    if (!activeItem) {
      setPreviewHighlight(null);
      setActivePreviewField(null);
      return;
    }
    const query = String(activeItem?.["Item Name"] || "").trim();
    const field = previewHighlight.field || "Item Name";
    const fieldTarget = previewHitTargets.find(
      (target) => target.itemKey === activeItem.key && target.field === field
    );
    const sourceBbox =
      fieldTarget?.sourceBbox || activeItem?.source_bbox || null;
    const normalizedQuery = normalizePreviewQuery(query);
    const occurrence = pageItems
      .slice(0, activeIndex)
      .filter(
        (item) =>
          normalizedQuery &&
          normalizePreviewQuery(item?.["Item Name"]) === normalizedQuery
      ).length;
    if (
      query !== previewHighlight.query ||
      sourceBbox !== previewHighlight.sourceBbox ||
      occurrence !== previewHighlight.occurrence ||
      field !== previewHighlight.field
    ) {
      setPreviewHighlight((current) =>
        current?.itemKey === activeItem.key
          ? { ...current, query, sourceBbox, occurrence, field }
          : current
      );
    }
  }, [invoiceData, previewHighlight, previewHitTargets, selectedPage]);

  useEffect(() => {
    setPreviewHighlight(null);
    setActivePreviewField(null);
  }, [selectedPage]);

  useEffect(() => {
    const keys = (invoiceData?.extracted_invoice_data?.[selectedPage - 1]
      ?.treatments || []).map((treatment) => String(treatment.key));
    setOpenTreatmentKeys(keys);
  }, [invoiceData?.id, selectedPage]);


  const handleSwitchManualSum = (checked) => {
    setSumManual(
      typeof checked === "boolean" ? checked : (prevState) => !prevState
    );
  };

  const handleSwitchOverride = (field) => {
    setManualOverrides((prevState) => ({
      ...prevState,
      [field]: !prevState[field],
    }));
  };

  // Effective quantity-aware subtotal (Σ price × qty) as a Number.
  const computeSubtotal = () => computeQuantityAwareTotal(invoiceData);

  const computeSubtotalAfterDiscount = () => {
    const discount = Math.abs(parseFloat(sumValues?.total_discount_amount) || 0);
    return computeSubtotal() - discount;
  };

  const openNotesModel = () => {
    setIsNotesModalVisible(true);
  };

  const handleNotesModalCancel = () => {
    // notes_log entries save individually, so closing the thread is never a
    // destructive "discard" — no spurious "not saved" toast (roast R19).
    setIsNotesModalVisible(false);
  };

  const handleNotesSaved = (text, updatedLog) => {
    setNotes(text);
    setInvoiceData((prev) =>
      prev ? { ...prev, notes: text, notes_log: updatedLog } : prev
    );
  };

  // R21: apiClient centralizes auth headers + 401/force-logout.
  const handleExcelDownload = async () => {
    try {
      const response = await apiClient.get(`/document/export/${documentId}`);
      handleDownload(response?.result?.sas_document_link);

      if (environment.APP_ENVIRONMENT !== 'alabusTesting' && environment.APP_ENVIRONMENT !== 'eponaDevelopment' && environment.APP_ENVIRONMENT !== 'eponaTesting' && environment.APP_ENVIRONMENT !== 'eponaProduction') {
        // Disabling download excel for alabus client
        if (response?.result?.sas_excel_link) {
          setTimeout(async () => {
            await handleDownload(response?.result?.sas_excel_link);
          }, 3000);
        }
      }
    } catch (err) {
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else if (err.status === 400) {
        notification.error({
          message: t("notAssigned"),
        });
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
    }
  };

  useEffect(() => {
    const documentId = location.pathname.split("/")[2];

    loadedRef.current = false;
    setIsDirty(false);
    setLastSavedAt(null);
    setSelectedPage(1);
    setClassificationErrors({});
    setFeedbackStatuses({});
    feedbackIdempotencyKeysRef.current.clear();
    acknowledgedFeedbackRef.current.clear();
    try {
      const retryState = JSON.parse(
        sessionStorage.getItem(feedbackRetryStorageKey(documentId)) || "{}"
      );
      feedbackIdempotencyKeysRef.current = new Map(retryState.keys || []);
      acknowledgedFeedbackRef.current = new Set(retryState.acknowledged || []);
    } catch (storageError) {
      sessionStorage.removeItem(feedbackRetryStorageKey(documentId));
    }
    setExtractionId(documentId);
    getDocument(documentId);
    setDocumentId(documentId);
  }, [location.pathname]);

  const getDocument = async (documentId) => {
    setLoading(true);

    try {
      const response = await apiClient.get(`/document/${documentId}`);

      const invoiceDetails = prepareInvoiceForEditor(response?.result);

      setInvoiceData(invoiceDetails);
      setInitialInvoiceData(cloneInvoiceData(invoiceDetails));

      const updatedValues = {};

      for (let key in invoiceDetails.total_sum_values) {
        if (invoiceDetails.total_sum_values.hasOwnProperty(key)) {
          let value = invoiceDetails.total_sum_values[key];

          value = parseFloat(Number(value)?.toFixed(2));

          updatedValues[key] = value;
        }
      }

      setSumValues(updatedValues);
      setNotes(invoiceDetails.notes);
      setSumManual(invoiceDetails?.total_sum_values?.is_manual);
      const loadedIsManual = !!invoiceDetails?.total_sum_values?.is_manual;
      setManualOverrides({
        calculated_total_sum: loadedIsManual,
        calculated_total_sum_after_discount: loadedIsManual,
      });
      const invoiceImages = response?.result?.extracted_invoice_data?.map(
        (item) => {
          return { id: item.page_no, image_url: item.sasUrl };
        }
      );

      setImages(invoiceImages);

      const logged_user = sessionStorage.getItem("user_id");
      if (logged_user === response?.result?.assigned_to) {
        setIsOwner(true);
      } else {
        setIsOwner(false);
      }
      setLoading(false);

      // mark document as loaded: state set above must not count as user edits
      suppressDirtyRef.current = true;
      loadedRef.current = true;
      setIsDirty(false);

      // offer to restore an unsaved local draft
      try {
        const draftRaw = localStorage.getItem(draftStorageKey(documentId));
        if (draftRaw) {
          const draft = JSON.parse(draftRaw);
          Modal.confirm({
            title: t("restoreDraftTitle"),
            content: t("restoreDraftContent"),
            okText: t("restore"),
            cancelText: t("discard"),
            onOk: () => {
              setInvoiceData(draft.invoiceData);
              setSumValues(draft.sumValues);
              setSumManual(!!draft.isSumManual);
              if (draft.manualOverrides) {
                setManualOverrides(draft.manualOverrides);
              }
              setIsDirty(true);
            },
            onCancel: () => {
              localStorage.removeItem(draftStorageKey(documentId));
            },
          });
        }
      } catch (draftErr) {
        localStorage.removeItem(draftStorageKey(documentId));
      }
    } catch (err) {
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else {
        notification.error({
          message: t("documentListError"),
          description: t("genericError"),
        });
      }
    }
    setLoading(false);
  };

  const getInitialDocument = async (documentId) => {
    setLoading(true);
    try {
      const response = await apiClient.get(`/document/initial/${documentId}`);
      setLoading(false);

      notification.success({
        message: t("DocumentResetToInitial"),
      });

      const invoiceDetails = prepareInvoiceForEditor({
        ...response?.result,
        extracted_invoice_data: response?.result?.extracted_invoice_data_initial,
        general_information: response?.result?.general_information_initial,
        total_sum_values: response?.result?.total_sum_values_initial,
      });

      setInvoiceData(invoiceDetails);
      setInitialInvoiceData(cloneInvoiceData(invoiceDetails));
      setSumValues(invoiceDetails?.total_sum_values);
      setNotes(invoiceDetails.notes);
      const resetIsManual = !!invoiceDetails?.total_sum_values?.is_manual;
      setManualOverrides({
        calculated_total_sum: resetIsManual,
        calculated_total_sum_after_discount: resetIsManual,
      });
      const invoiceImages = response?.result?.extracted_invoice_data?.map(
        (item) => {
          return { id: item.page_no, image_url: item.sasUrl };
        }
      );

      setImages(invoiceImages);
      suppressDirtyRef.current = true;
      setIsDirty(false);
      localStorage.removeItem(draftStorageKey(documentId));
    } catch (err) {
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
      setLoading(false);
    }
  };

  // dirty tracking + draft autosave: any change to editable state after load
  // marks the form dirty and persists a local draft (debounced)
  useEffect(() => {
    if (!loadedRef.current) return;
    if (suppressDirtyRef.current) {
      suppressDirtyRef.current = false;
      return;
    }
    setIsDirty(true);
    clearTimeout(draftTimerRef.current);
    draftTimerRef.current = setTimeout(() => {
      try {
        localStorage.setItem(
          draftStorageKey(documentId),
          JSON.stringify({
            invoiceData,
            sumValues,
            isSumManual,
            manualOverrides,
            savedAt: Date.now(),
          })
        );
      } catch (storageErr) {
        // storage full or unavailable: draft simply is not persisted
      }
    }, 800);
  }, [invoiceData, sumValues, isSumManual]);

  // warn before closing/reloading the tab with unsaved changes
  useEffect(() => {
    const handleBeforeUnload = (e) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = "";
      }
    };
    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty]);

  // keyboard shortcuts: Ctrl+S save, Ctrl+Enter save & next, Ctrl+E complete
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!(e.ctrlKey || e.metaKey)) return;
      const key = e.key.toLowerCase();
      if (key === "s") {
        e.preventDefault();
        keyActionsRef.current.save?.();
      } else if (key === "enter") {
        e.preventDefault();
        keyActionsRef.current.saveNext?.();
      } else if (key === "e") {
        e.preventDefault();
        keyActionsRef.current.complete?.();
      }
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  // R16: count fields that need attention. Empty required fields already carry
  // .bg-invoiceorange; low-confidence line items carry .attention-lowconf. Counted
  // from the live DOM so the number always matches exactly what is highlighted.
  useEffect(() => {
    const el = invoiceFormRef.current;
    if (!el) return;
    const count = el.querySelectorAll(".bg-invoiceorange, .attention-lowconf").length;
    setAttentionCount((prev) => (prev === count ? prev : count));
  });

  // R16: jump to the next empty / low-confidence field (scroll + focus + outline).
  const jumpToNextAttentionField = () => {
    const container = invoiceFormRef.current;
    if (!container) return;
    const fields = Array.from(
      container.querySelectorAll(".bg-invoiceorange, .attention-lowconf")
    ).filter((el) => el.offsetParent !== null); // only fields actually visible
    if (!fields.length) return;
    attentionIndexRef.current = (attentionIndexRef.current + 1) % fields.length;
    const target = fields[attentionIndexRef.current];
    container
      .querySelectorAll(".attention-focus-ring")
      .forEach((el) => el.classList.remove("attention-focus-ring"));
    target.classList.add("attention-focus-ring");
    target.scrollIntoView({ behavior: "smooth", block: "center" });
    const focusable = target.matches("input, textarea, [tabindex]")
      ? target
      : target.querySelector("input, textarea, .ant-select-selector, [tabindex]");
    if (focusable && focusable.focus) focusable.focus({ preventScroll: true });
  };

  // R16: Alt+N keyboard shortcut for the attention jump.
  // Use e.code (KeyN) so layout/locale Alt-combos still match; ignore when
  // the user is composing text with AltGr on some keyboards (ctrlKey set).
  useEffect(() => {
    const onKeyDown = (e) => {
      if (!e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.code !== "KeyN" && (e.key || "").toLowerCase() !== "n") return;
      e.preventDefault();
      jumpToNextAttentionField();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, []);

  const onDragEnd = (result) => {
    const { source, destination, type } = result;

    if (!destination) {
      return;
    }

    if (type === "treatment") {
      setInvoiceData((prevState) => ({
        ...prevState,
        extracted_invoice_data: prevState.extracted_invoice_data.map(
          (pageData, index) => {
            if (index === selectedPage - 1) {
              // Reorder treatments
              let reordered = Reorder(
                pageData.treatments,
                source.index,
                destination.index
              );
              // Update treatment_order based on new order
              reordered = reordered.map((treatment, idx) => ({
                ...treatment,
                treatment_order: idx + 1,
              }));
              return {
                ...pageData,
                treatments: reordered,
              };
            }
            return pageData;
          }
        ),
      }));
    } else {
      setInvoiceData((prevState) => ({
        ...prevState,
        extracted_invoice_data: prevState.extracted_invoice_data.map(
          (pageData, index) => {
            if (index === selectedPage - 1) {
              const updatedTreatments = pageData.treatments.map((treatment) => {
                if (treatment.key === source.droppableId) {
                  return {
                    ...treatment,
                    Items: Reorder(
                      treatment.Items,
                      source.index,
                      destination.index
                    ),
                  };
                }
                return treatment;
              });

              return {
                ...pageData,
                treatments: updatedTreatments,
              };
            }
            return pageData;
          }
        ),
      }));
    }
  };

  const handleOnChangePage = (page) => {
    setSelectedPage(page);
  };

  const handleOnClickAssign = () => {
    if (isOwner) {
      setConfirmationPopup({
        isShow: true,
        type: "revoke",
        treatmentKey: null,
        itemKey: null,
        assigned_to: null,
      });
    } else {
      setConfirmationPopup({
        isShow: true,
        type: "assign",
        treatmentKey: null,
        itemKey: null,
        assigned_to: invoiceData.assigned_user,
      });
    }
  };

  const handleOnAssignOwner = async () => {
    let notificationMessage;
    if (isOwner) {
      notificationMessage = t("revokeDocument");
    } else {
      notificationMessage = t("successDocumentProgressStatus");
    }
    try {
      await apiClient.patch(`/document/status/${documentId}`, {});

      notification.success({
        message: notificationMessage,
      });
      setIsOwner(!isOwner);
      getDocument(documentId);
    } catch (err) {
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
    }
  };

  const addTreatment = () => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index === selectedPage - 1) {
            return {
              ...pageData,
              treatments: [
                ...pageData.treatments,
                {
                  treatment_order:
                    prevState.extracted_invoice_data[selectedPage - 1]
                      .treatments.length + 1,
                  "Pet Name": "",
                  "Treatment Date": "",
                  "Diagnose": "",
                  key: uuid(),
                  Items: [
                    createLineItemWithFeedbackSnapshot({
                      "Item Name": "",
                      "Item Quantity": "",
                      "Item Price": "",
                      VAT: null,
                      item_order: 1,
                    }),
                  ],
                  sub_total: 0,
                },
              ],
            };
          }
          return pageData;
        }
      ),
    }));
  };

  const removeTreatment = (treatmentKey) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index === selectedPage - 1) {
            // Remove the treatment and update treatment_order
            const filteredTreatments = pageData.treatments.filter(
              (treatment) => treatment.key !== treatmentKey
            );
            const reorderedTreatments = filteredTreatments.map((treatment, idx) => ({
              ...treatment,
              treatment_order: idx + 1,
            }));
            return {
              ...pageData,
              treatments: reorderedTreatments,
            };
          }
          return pageData;
        }
      ),
    }));
  };

  const addItemToTreatment = (treatmentKey) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState?.extracted_invoice_data?.map(
        (pageData, index) => {
          if (index === selectedPage - 1) {
            return {
              ...pageData,
              treatments: pageData.treatments.map((treatment) => {
                if (treatment.key === treatmentKey) {
                  // Calculate the next item_order
                  const items = treatment.Items || [];
                  const maxOrder = items.length > 0
                    ? Math.max(...items.map(item => item.item_order || 0))
                    : 0;
                  return {
                    ...treatment,
                    Items: [
                      ...items,
                      createLineItemWithFeedbackSnapshot({
                        "Item Name": "",
                        "Item Quantity": "",
                        "Item Price": "",
                        VAT: null,
                        isNew: true,
                        item_order: maxOrder + 1,
                      }),
                    ],
                  };
                }
                return treatment;
              }),
            };
          }
          return pageData;
        }
      ),
    }));
  };

  const updateInvoiceItem = (item, inputName, newValue) => {
    const nextItem = {
      ...item,
      [inputName]: newValue,
    };

    if (inputName === "feedback_change_reason" && !newValue) {
      nextItem.feedback_change_context = "";
    }

    if (
      ["category", "exclusion_subcode"].includes(inputName) &&
      !hasPendingClassificationChange(nextItem)
    ) {
      nextItem.feedback_change_reason = "";
      nextItem.feedback_change_context = "";
    }

    return nextItem;
  };

  const updateTreatmentItems = (treatment, itemKey, inputName, newValue) => ({
    ...treatment,
    Items: treatment.Items.map((item) => {
      if (item.key !== itemKey) {
        return item;
      }

      if (inputName === "Item Price") {
        const regex = /^-?\d*\.?\d{0,2}$/;

        if (!regex.test(newValue)) {
          return item;
        }
      }

      return updateInvoiceItem(item, inputName, newValue);
    }),
  });

  const removeItemFromTreatment = (treatmentKey, itemKey) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index === selectedPage - 1) {
            return {
              ...pageData,
              treatments: pageData.treatments.map((treatment) => {
                if (treatment.key === treatmentKey) {
                  // Remove the item and update item_order
                  const filteredItems = treatment.Items.filter(
                    (item) => item.key !== itemKey
                  );
                  const reorderedItems = filteredItems.map((item, idx) => ({
                    ...item,
                    item_order: idx + 1,
                  }));
                  return {
                    ...treatment,
                    Items: reorderedItems,
                  };
                }
                return treatment;
              }),
            };
          }
          return pageData;
        }
      ),
    }));
  };

  const updateTreatmentContent = (
    treatmentKey,
    itemKey,
    inputName,
    newValue
  ) => {
    if (
      itemKey &&
      [
        "category",
        "exclusion_subcode",
        "feedback_change_reason",
        "feedback_change_context",
      ].includes(inputName)
    ) {
      setClassificationErrors((current) => {
        if (!current[itemKey]) return current;
        const next = { ...current };
        delete next[itemKey];
        return next;
      });
      setFeedbackStatuses((current) => {
        if (!current[itemKey]) return current;
        const next = { ...current };
        delete next[itemKey];
        return next;
      });
    }
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, pageIndex) => {
          if (pageIndex === selectedPage - 1) {
            return {
              ...pageData,
              treatments: pageData.treatments.map((treatment) => {
                if (treatment.key === treatmentKey) {
                  if (
                    inputName === "Pet Name" ||
                    inputName === "Treatment Date" ||
                    inputName === "Diagnose"
                  ) {
                    return {
                      ...treatment,
                      [inputName]: newValue,
                    };
                  } else if (inputName === "Item Price") {
                    return updateTreatmentItems(
                      treatment,
                      itemKey,
                      inputName,
                      newValue
                    );
                  } else {
                    return updateTreatmentItems(
                      treatment,
                      itemKey,
                      inputName,
                      newValue
                    );
                  }
                }
                return treatment;
              }),
            };
          }
          return pageData;
        }
      ),
    }));
  };

  const onChangeGenreralInfo = (e) => {
    const { name, value } = e.target;

    setInvoiceData((prevState) => ({
      ...prevState,
      general_information: {
        ...prevState.general_information,
        [name]: value,
      },
    }));
  };

  // Set several general_information fields at once (autocomplete prefill).
  const setGeneralInfoFields = (fields) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      general_information: { ...prevState.general_information, ...fields },
    }));
  };

  // R20: Swiss PLZ = 4 digits; empty is allowed (flagged separately as missing).
  const validatePlz = (name, value) => {
    const trimmed = (value ?? "").toString().trim();
    const isValid = trimmed === "" || /^\d{4}$/.test(trimmed);
    setPlzErrors((prev) => ({ ...prev, [name]: !isValid }));
    return isValid;
  };

  // R20: warn when the same invoice reference + policyholder already exists.
  const handleInvoiceReferenceBlur = async () => {
    const reference = invoiceData?.general_information?.["Invoice Reference"]
      ?.toString()
      .trim();
    const policyholder = invoiceData?.general_information?.["Policyholder Name"]
      ?.toString()
      .trim();
    if (!reference || !policyholder) {
      setDuplicateInfo(null);
      return;
    }
    setCheckingDuplicate(true);
    try {
      const res = await documentService.checkDuplicate({
        reference,
        policyholder,
        excludeId: documentId,
      });
      setDuplicateInfo(res?.result || { duplicate: false });
    } catch (err) {
      setDuplicateInfo(null); // best-effort; never block editing
    } finally {
      setCheckingDuplicate(false);
    }
  };

  // R20: selecting an autocomplete suggestion fills the matching address fields.
  const handleVetSelect = (value) => {
    const match = vetOptions.find((o) => o.name === value);
    if (!match) return;
    setGeneralInfoFields({
      "Vet Name": match.name,
      vet_street_address: match.street || "",
      vet_city: match.city || "",
      vet_zipcode: match.zipcode || "",
    });
    if (match.zipcode) validatePlz("vet_zipcode", match.zipcode);
  };

  const handlePolicyholderSelect = (value) => {
    const match = policyholderOptions.find((o) => o.name === value);
    if (!match) return;
    setGeneralInfoFields({
      "Policyholder Name": match.name,
      policy_holder_street_address: match.street || "",
      policy_holder_city: match.city || "",
      policy_holder_zipcode: match.zipcode || "",
    });
    if (match.zipcode) validatePlz("policy_holder_zipcode", match.zipcode);
  };

  // R20: load distinct vet/policyholder values for autocomplete (owners only).
  useEffect(() => {
    if (!isOwner) return;
    let cancelled = false;
    (async () => {
      try {
        const [vetRes, phRes] = await Promise.all([
          documentService.lookup("vet"),
          documentService.lookup("policyholder"),
        ]);
        if (cancelled) return;
        setVetOptions(vetRes?.result || []);
        setPolicyholderOptions(phRes?.result || []);
      } catch (err) {
        // autocomplete is best-effort; ignore lookup failures
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOwner]);

  const handleOnChangeSumValues = (e) => {
    let value = String(e.target.value ?? "").trim();
    const regex = /^-?\d*\.?\d{0,2}$/;

    if (regex.test(value)) {

      setSumValues((preState) => {
        return {
          ...preState,
          [e.target.name]: value,
        };
      });
    }
  };
  const calculateSubTotal = (treatment) => {
    const subTotal = treatment.Items.reduce((acc, curr) => {
      // R14: quantity-aware line total (price × qty) when the flag is on.
      if (enableQuantityTotals) {
        const lineTotal = getLineTotal(curr);
        return isNaN(lineTotal) ? acc : acc + lineTotal;
      }
      const price = parseFloat(curr["Item Price"]);
      if (!isNaN(price)) {
        return acc + price;
      }
      return acc;
    }, 0);

    return subTotal.toFixed(2);
  };

  const calculateTotalSum = () => {
    let totalItemsPrice = 0;

    invoiceData?.extracted_invoice_data?.forEach((pageData) => {
      pageData.treatments.forEach((treatment) => {
        treatment.Items.forEach((item) => {
          const price = parseFloat(item["Item Price"]);
          if (!isNaN(price)) {
            totalItemsPrice += price;
          }
        });
      });
    });

    return totalItemsPrice.toFixed(2);
  };

  const calculateManualSum = () => {
    return sumValues?.calculated_total_sum_after_discount_manual;
  };

  const calculateAutoSumAfterDiscount = () => {
    const discountAmount = Math.abs(parseFloat(sumValues?.total_discount_amount) || 0); 
    const sum = calculateTotalSum() - discountAmount;
  
    const result = isNaN(sum) ? null : parseFloat(sum.toFixed(2));
  
    return result;
  };
  
  
  

  const handleOnReset = () => {
    suppressDirtyRef.current = true;
    setInvoiceData(cloneInvoiceData(initialInvoiceData));
    setIsDirty(false);
    localStorage.removeItem(draftStorageKey(documentId));
  };

  const handleConfirmationPopup = (type, treatmentKey, itemKey) => {
    setConfirmationPopup({
      isShow: true,
      type,
      treatmentKey: treatmentKey || null,
      itemKey: itemKey || null,
    });
  };

  const handleOnDeleteConfirmation = () => {
    if (confirmationPopup.type === "treatment") {
      removeTreatment(confirmationPopup.treatmentKey);
    } else if (confirmationPopup.type === "line-item") {
      removeItemFromTreatment(
        confirmationPopup.treatmentKey,
        confirmationPopup.itemKey
      );
    } else if (confirmationPopup.type === "resetToInitial") {
      getInitialDocument(documentId);
    } else if (
      confirmationPopup.type === "assign" ||
      confirmationPopup.type === "revoke"
    ) {
      handleOnAssignOwner();
    } else if (confirmationPopup.type === "reset") {
      handleOnReset();
    }
    setConfirmationPopup({
      isShow: false,
      type: null,
      treatmentKey: null,
      itemKey: null,
    });
  };

  const handleOnDeleteCancel = () => {
    setConfirmationPopup({
      isShow: false,
      type: null,
      key: null,
    });
  };

  // saveInvoice persists corrections without re-fetching the document,
  // so scroll position, selected page and collapse state are preserved.
  // Returns true on success.
  const saveInvoice = async () => {
    if (!isOwner) {
      notification.warning({ message: t("notAssigned") });
      return false;
    }

    const focusFirstClassificationError = () => {
      window.setTimeout(() => {
        const firstInvalid = invoiceFormRef.current?.querySelector(
          '[data-feedback-error="true"][tabindex="-1"], [data-feedback-error="true"] .ant-select-selector, [data-feedback-error="true"] textarea'
        );
        firstInvalid?.focus();
        firstInvalid?.scrollIntoView({ behavior: "smooth", block: "center" });
      }, 0);
    };

    const classificationChanges = collectClassificationChanges(
      invoiceData,
      classificationMetadata
    );
    const nextClassificationErrors =
      getInvoiceClassificationValidationErrors(
        invoiceData,
        classificationMetadata
      );
    if (Object.keys(nextClassificationErrors).length) {
      setClassificationErrors(nextClassificationErrors);
      notification.error({
        message: t("feedbackValidationTitle"),
        description: t("feedbackValidationDescription"),
      });
      focusFirstClassificationError();
      return false;
    }
    setClassificationErrors({});

    const {
      submissions: feedbackSubmissions,
      skipped: skippedFeedback,
    } = buildClassificationFeedbackSubmissions(classificationChanges);
    if (skippedFeedback.length) {
      setClassificationErrors(
        skippedFeedback.reduce(
          (errors, { change }) => ({
            ...errors,
            [change.item.key]: { identity: "feedbackIdentityRequired" },
          }),
          {}
        )
      );
      notification.error({
        message: t("feedbackSaveFailed"),
        description: t("feedbackIdentityRequired"),
      });
      focusFirstClassificationError();
      return false;
    }

    const reqData = prepareInvoiceUpdatePayload(invoiceData);
    const reqSumValues = { ...sumValues };

    // Convert numeric values to float
    reqSumValues.invoice_total_sum_price_without_vat = parseFloat(reqSumValues.invoice_total_sum_price_without_vat) || 0;
    reqSumValues.invoice_total_sum_price_with_vat = parseFloat(reqSumValues.invoice_total_sum_price_with_vat) || 0;
    reqSumValues.calculated_total_sum = parseFloat(reqSumValues.calculated_total_sum) || 0;
    reqSumValues.total_discount_amount = parseFloat(reqSumValues.total_discount_amount) || 0;
    reqSumValues.calculated_total_sum_after_discount = parseFloat(reqSumValues.calculated_total_sum_after_discount) || 0;
    reqSumValues.calculated_total_sum_manual = parseFloat(reqSumValues.calculated_total_sum_manual) || 0;
    reqSumValues.calculated_total_sum_after_discount_manual = parseFloat(reqSumValues.calculated_total_sum_after_discount_manual) || 0;

    reqData.total_sum_values = reqSumValues;

    if (enableQuantityTotals) {
      // R14: per-field overrides; `is_manual` stays back-compat (true if any override).
      const subtotal = computeSubtotal();
      const discount = Math.abs(parseFloat(sumValues?.total_discount_amount) || 0);
      reqData.total_sum_values.is_manual =
        manualOverrides.calculated_total_sum ||
        manualOverrides.calculated_total_sum_after_discount;
      reqData.total_sum_values.calculated_total_sum = manualOverrides.calculated_total_sum
        ? parseFloat(sumValues?.calculated_total_sum_manual) || 0
        : parseFloat(subtotal.toFixed(2));
      reqData.total_sum_values.calculated_total_sum_after_discount =
        manualOverrides.calculated_total_sum_after_discount
          ? parseFloat(sumValues?.calculated_total_sum_after_discount_manual) || 0
          : parseFloat((subtotal - discount).toFixed(2));
    } else {
      reqData.total_sum_values.is_manual = isSumManual;

      if (!isSumManual) {
        reqData.total_sum_values.calculated_total_sum = parseFloat(calculateTotalSum()) || 0;
        reqData.total_sum_values.calculated_total_sum_after_discount = parseFloat(
          (calculateTotalSum() - Math.abs(parseFloat(sumValues?.total_discount_amount) ||0))
            .toFixed(2)
        );
      }
    }

    setLoading(true);

    try {
      for (const { change, payload } of feedbackSubmissions) {
        const signature = getFeedbackSignature(payload);
        setFeedbackStatuses((current) => ({
          ...current,
          [change.item.key]: { state: "pending" },
        }));
        if (!acknowledgedFeedbackRef.current.has(signature)) {
          let idempotencyKey = feedbackIdempotencyKeysRef.current.get(signature);
          if (!idempotencyKey) {
            idempotencyKey = uuid();
            feedbackIdempotencyKeysRef.current.set(signature, idempotencyKey);
            persistFeedbackRetryState(documentId);
          }
          try {
            await apiClient.post(
              `/document/${documentId}/classification-feedback`,
              payload,
              undefined,
              { "Idempotency-Key": idempotencyKey }
            );
            acknowledgedFeedbackRef.current.add(signature);
            persistFeedbackRetryState(documentId);
          } catch (feedbackError) {
            const messageKey =
              feedbackError?.status === 409
                ? "feedbackConflict"
                : feedbackError?.status === 412
                ? "feedbackStale"
                : "feedbackNetworkError";
            setFeedbackStatuses((current) => ({
              ...current,
              [change.item.key]: {
                state: "error",
                messageKey,
              },
            }));
            notification.error({
              message: t("feedbackSaveFailed"),
              description: t(messageKey),
            });
            setLoading(false);
            return false;
          }
        }
        setFeedbackStatuses((current) => ({
          ...current,
          [change.item.key]: { state: "saved" },
        }));
      }

      await apiClient.put(`/document/update/${extractionId}`, reqData);
      setLoading(false);
      // keep local state as the source of truth: corrections accepted by
      // the server, no context-destroying refetch needed
      suppressDirtyRef.current = true;
      const savedInvoiceData =
        markInvoiceClassificationBaselinesSaved(invoiceData);
      setInvoiceData(savedInvoiceData);
      setInitialInvoiceData(cloneInvoiceData(savedInvoiceData));
      setFeedbackStatuses((current) => {
        const next = { ...current };
        classificationChanges.forEach(({ item }) => {
          next[item.key] = { state: "saved" };
        });
        return next;
      });
      setIsDirty(false);
      setLastSavedAt(new Date());
      localStorage.removeItem(draftStorageKey(documentId));
      sessionStorage.removeItem(feedbackRetryStorageKey(documentId));
      feedbackIdempotencyKeysRef.current.clear();
      acknowledgedFeedbackRef.current.clear();
      return true;
    } catch (err) {
      setLoading(false);
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
      return false;
    }
  };

  const handleOnUpdate = async () => {
    const ok = await saveInvoice();
    if (ok) {
      notification.success({
        message: t("UpdateSuccess"),
      });
    }
  };

  const handleTranslateReasonings = async () => {
    if (!invoiceData?.extracted_invoice_data?.length) return;

    const hasReasoning = invoiceData.extracted_invoice_data.some((page) =>
      (page?.treatments || []).some((treatment) =>
        (treatment?.Items || []).some((item) =>
          Boolean((item?.reasoning || item?.Reasoning || "").toString().trim())
        )
      )
    );

    if (!hasReasoning) {
      notification.info({ message: t("translateReasoningsEmpty") });
      return;
    }

    try {
      setTranslatingReasonings(true);
      const result = (await apiClient.post(`/document/translate-reasonings`, {
        id: documentId || extractionId,
        extractedInvoiceData: invoiceData.extracted_invoice_data,
        language: currentLanguage,
      }))?.result;

      if (result?.error || !result?.extracted_invoice_data) {
        throw new Error("translate_failed");
      }

      setInvoiceData((prev) => ({
        ...prev,
        extracted_invoice_data: result.extracted_invoice_data,
      }));
      setIsDirty(true);
      notification.success({
        message: t("translateReasoningsSuccess"),
      });
    } catch (err) {
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
    } finally {
      setTranslatingReasonings(false);
    }
  };

  const handleSaveAndNext = async () => {
    const ok = await saveInvoice();
    if (!ok) return;
    notification.success({
      message: t("UpdateSuccess"),
    });
    try {
      // oldest not-yet-started document = FIFO processing order
      const res = await apiClient.post(
        `/document?page=1&limit=1&updation_status=not_yet_started&sortBy=ASC&sortField=received_at`,
        {}
      );
      const next = res?.result?.data?.[0];
      if (next && next.id !== documentId) {
        navigate(`/invoice/${next.id}`);
      } else {
        notification.info({ message: t("queueEmpty") });
        navigate("/invoice");
      }
    } catch (err) {
      navigate("/invoice");
    }
  };

  // mark the document completed WITHOUT downloading any files
  const markCompleted = async () => {
    setLoading(true);
    try {
      await apiClient.get(`/document/export/${documentId}`);
      setLoading(false);
      notification.success({ message: t("markedCompleted") });
      localStorage.removeItem(draftStorageKey(documentId));
      suppressDirtyRef.current = true;
      setIsDirty(false);
      navigate("/invoice");
    } catch (err) {
      setLoading(false);
      if (err?.status === 401) {
        // forceLogout already shows a single session-expired toast
      } else if (err?.status === 400) {
        notification.error({
          message: t("notAssigned"),
        });
      } else {
        notification.error({
          message: t("genericError"),
        });
      }
    }
  };

  const handleComplete = () => {
    if (!isOwner) {
      notification.warning({ message: t("notAssigned") });
      return;
    }
    Modal.confirm({
      title: t("completeConfirmTitle"),
      content: t("completeConfirmContent"),
      okText: t("complete"),
      cancelText: t("cancel"),
      onOk: async () => {
        if (isDirty) {
          const ok = await saveInvoice();
          if (!ok) return;
        }
        markCompleted();
      },
    });
  };

  // immutable line-item reorder, owned by Invoice state (fixes the old
  // prop mutation in LineItem's drag end)
  const reorderItemsInTreatment = (treatmentKey, newItems) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index === selectedPage - 1) {
            return {
              ...pageData,
              treatments: pageData.treatments.map((treatment) =>
                treatment.key === treatmentKey
                  ? { ...treatment, Items: newItems }
                  : treatment
              ),
            };
          }
          return pageData;
        }
      ),
    }));
  };

  // R18: move a line item to another treatment on the current page (no DnD edge cases).
  const moveItemToTreatment = (sourceTreatmentKey, itemKey, targetTreatmentKey) => {
    if (sourceTreatmentKey === targetTreatmentKey) return;
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index !== selectedPage - 1) return pageData;
          let movedItem = null;
          const withoutItem = pageData.treatments.map((treatment) => {
            if (treatment.key !== sourceTreatmentKey) return treatment;
            const Items = treatment.Items.filter((it) => {
              if (it.key === itemKey) {
                movedItem = it;
                return false;
              }
              return true;
            });
            return {
              ...treatment,
              Items: Items.map((it, i) => ({ ...it, item_order: i + 1 })),
            };
          });
          if (!movedItem) return pageData;
          const treatments = withoutItem.map((treatment) => {
            if (treatment.key !== targetTreatmentKey) return treatment;
            const newItem = { ...movedItem, item_order: treatment.Items.length + 1 };
            return { ...treatment, Items: [...treatment.Items, newItem] };
          });
          return { ...pageData, treatments };
        }
      ),
    }));
  };

  // R18: duplicate a line item in place (fresh key + feedback snapshot).
  const duplicateItemInTreatment = (treatmentKey, itemKey) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index !== selectedPage - 1) return pageData;
          const treatments = pageData.treatments.map((treatment) => {
            if (treatment.key !== treatmentKey) return treatment;
            const srcIndex = treatment.Items.findIndex((it) => it.key === itemKey);
            if (srcIndex === -1) return treatment;
            const duplicatedSource = sanitizeDuplicatedClassificationItem(
              treatment.Items[srcIndex]
            );
            delete duplicatedSource.source_bbox;
            const copy = {
              ...duplicatedSource,
              key: uuid(),
              isNew: true,
            };
            const Items = [...treatment.Items];
            Items.splice(srcIndex + 1, 0, copy);
            return {
              ...treatment,
              Items: Items.map((it, i) => ({ ...it, item_order: i + 1 })),
            };
          });
          return { ...pageData, treatments };
        }
      ),
    }));
  };

  // R18: keyboard-accessible reorder (direction: -1 up, +1 down).
  const moveItemUpDown = (treatmentKey, itemKey, direction) => {
    setInvoiceData((prevState) => ({
      ...prevState,
      extracted_invoice_data: prevState.extracted_invoice_data.map(
        (pageData, index) => {
          if (index !== selectedPage - 1) return pageData;
          const treatments = pageData.treatments.map((treatment) => {
            if (treatment.key !== treatmentKey) return treatment;
            const idx = treatment.Items.findIndex((it) => it.key === itemKey);
            const targetIdx = idx + direction;
            if (idx === -1 || targetIdx < 0 || targetIdx >= treatment.Items.length) {
              return treatment;
            }
            const Items = [...treatment.Items];
            [Items[idx], Items[targetIdx]] = [Items[targetIdx], Items[idx]];
            return {
              ...treatment,
              Items: Items.map((it, i) => ({ ...it, item_order: i + 1 })),
            };
          });
          return { ...pageData, treatments };
        }
      ),
    }));
  };

  const handleOnBack = () => {
    if (isDirty) {
      Modal.confirm({
        title: t("unsavedChangesTitle"),
        content: t("unsavedChangesContent"),
        okText: t("leave"),
        cancelText: t("stay"),
        onOk: () => {
          localStorage.removeItem(draftStorageKey(documentId));
          navigate("/invoice");
        },
      });
    } else {
      navigate("/invoice");
    }
  };

  keyActionsRef.current = {
    save: handleOnUpdate,
    saveNext: handleSaveAndNext,
    complete: handleComplete,
  };
  const normalizeDecimal = (value) => {
    let valueStr = value.toString();
    if (valueStr.includes(".")) {
      let parts = valueStr.split(".");
      if (parts[1].length === 1) {
        valueStr = `${parts[0]}.${parts[1]}0`;
      }
    }
    return valueStr;
  };

  const verifyTotal = () => {
    const discountValue = Math.abs(parseFloat(sumValues?.total_discount_amount) || 0);

    let afterDiscount;
    if (enableQuantityTotals) {
      afterDiscount = manualOverrides.calculated_total_sum_after_discount
        ? calculateManualSum()
        : computeSubtotal() - discountValue;
    } else {
      afterDiscount =
        isSumManual === false
          ? calculateTotalSum() - discountValue
          : calculateManualSum();
    }

    let normalizedAfterDiscount = normalizeDecimal(afterDiscount);

    let normalizedInvoiceTotalWithVat = normalizeDecimal(
      sumValues?.invoice_total_sum_price_with_vat || 0
    );
    let normalizedInvoiceTotalWithoutVat = normalizeDecimal(
      sumValues?.invoice_total_sum_price_without_vat || 0
    );

    if (
      normalizedAfterDiscount === normalizedInvoiceTotalWithVat ||
      normalizedAfterDiscount === normalizedInvoiceTotalWithoutVat
    ) {
      return true;
    }
    return false;
  };

  const general_information = [
    {
      key: "1",
      label: (
        <span className="dark-text fs18 poppins-medium d-inline-block">
          {t("generalInformation")}
        </span>
      ),
      children: (
        <div>
          <div className="row mt20">
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("invoiceDate")}
              </label>
              <DateField
                name={"Invoice Date"}
                value={invoiceData?.general_information?.["Invoice Date"] || ""}
                onChange={(e) => onChangeGenreralInfo(e)}
                className={
                  invoiceData?.general_information?.["Invoice Date"]?.trim() ===
                    "" ||
                  invoiceData?.general_information?.["Invoice Date"] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("invoiceDate")}
                disabled={!isOwner}
              />{" "}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("invoiceReference")}
              </label>
              <Input
                name={"Invoice Reference"}
                value={invoiceData?.general_information?.["Invoice Reference"]}
                onChange={(e) => onChangeGenreralInfo(e)}
                onBlur={handleInvoiceReferenceBlur}
                className={
                  invoiceData?.general_information?.[
                    "Invoice Reference"
                  ]?.trim() === "" ||
                  invoiceData?.general_information?.["Invoice Reference"] ===
                    null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("invoiceReference")}
                disabled={!isOwner}
              />{" "}
              {checkingDuplicate && (
                <div className="fs12 time-gray mt5">{t("checkingDuplicate")}</div>
              )}
              {duplicateInfo?.duplicate && (
                <div className="duplicate-banner mt5" role="alert">
                  {t("duplicateWarning")}
                </div>
              )}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("language")}
              </label>
              <Input
                name={"invoice_language"}
                value={translateInvoiceLanguage(
                  t,
                  invoiceData?.general_information?.invoice_language
                )}
                onChange={(e) => onChangeGenreralInfo(e)}
                className={
                  invoiceData?.general_information?.invoice_language?.trim() ===
                    "" ||
                  invoiceData?.general_information?.invoice_language === null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("language")}
                disabled={!isOwner}
              />
            </div>
          </div>
          <div className="row mt15">
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("vetName")}
              </label>
              <AutoComplete
                value={invoiceData?.general_information?.["Vet Name"]}
                options={vetOptions.map((o) => ({ value: o.name, label: o.name }))}
                onChange={(val) =>
                  onChangeGenreralInfo({ target: { name: "Vet Name", value: val } })
                }
                onSelect={handleVetSelect}
                filterOption={(input, option) =>
                  (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                }
                className={
                  invoiceData?.general_information?.["Vet Name"]?.trim() ===
                    "" ||
                  invoiceData?.general_information?.["Vet Name"] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("vetName")}
                disabled={!isOwner}
                style={{ width: "100%" }}
              />{" "}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("vetStreet")}
              </label>
              <Input
                name={"vet_street_address"}
                value={invoiceData?.general_information?.["vet_street_address"]}
                onChange={(e) => onChangeGenreralInfo(e)}
                className={
                  invoiceData?.general_information?.[
                    "vet_street_address"
                  ]?.trim() === "" ||
                  invoiceData?.general_information?.["vet_street_address"] ===
                    null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("vetStreet")}
                disabled={!isOwner}
              />
            </div>
            <div className="col-md-4 col-12">
             <div className="row">
               <div className="col-md-5">
                 <label className="time-gray fs12 poppins-medium mb5 text-nowrap">
                   {t("postalCode")}
                 </label>
                 <Input
                   name={"vet_zipcode"}
                   value={invoiceData?.general_information?.["vet_zipcode"]}
                   onChange={(e) => {
                     onChangeGenreralInfo(e);
                     validatePlz("vet_zipcode", e.target.value);
                   }}
                   status={plzErrors.vet_zipcode ? "error" : ""}
                   className={
                     invoiceData?.general_information?.["vet_zipcode"]?.trim() ===
                       "" ||
                     invoiceData?.general_information?.["vet_zipcode"] === null
                       ? "bg-invoiceorange"
                       : ""
                   }
                   placeholder={t("postalCode")}
                   disabled={!isOwner}
                 />{" "}
                 {plzErrors.vet_zipcode && (
                   <div className="fs12 total-red mt5">{t("invalidPostalCode")}</div>
                 )}
               </div>
               <div className="col-md-7">
                 <label className="time-gray fs12 poppins-medium mb5">
                   {t("city")}
                 </label>
                 <Input
                   name={"vet_city"}
                   value={invoiceData?.general_information?.["vet_city"]}
                   onChange={(e) => onChangeGenreralInfo(e)}
                   className={
                     invoiceData?.general_information?.["vet_city"]?.trim() ===
                       "" ||
                     invoiceData?.general_information?.["vet_city"] === null
                       ? "bg-invoiceorange"
                       : ""
                   }
                   placeholder={t("city")}
                   disabled={!isOwner}
                 />{" "}
               </div>
             </div>
            </div>
          </div>
          <div className="row mt15 mb7">
            <div className="col-md-4">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("policyholderName")}
              </label>
              <AutoComplete
                value={invoiceData?.general_information?.["Policyholder Name"]}
                options={policyholderOptions.map((o) => ({ value: o.name, label: o.name }))}
                onChange={(val) =>
                  onChangeGenreralInfo({
                    target: { name: "Policyholder Name", value: val },
                  })
                }
                onSelect={handlePolicyholderSelect}
                filterOption={(input, option) =>
                  (option?.value ?? "").toLowerCase().includes(input.toLowerCase())
                }
                className={
                  invoiceData?.general_information?.[
                    "Policyholder Name"
                  ]?.trim() === "" ||
                  invoiceData?.general_information?.["Policyholder Name"] ===
                    null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("policyholderName")}
                disabled={!isOwner}
                style={{ width: "100%" }}
              />{" "}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("policyholderAddress")}
              </label>
              <Input
                name={"policy_holder_street_address"}
                value={
                  invoiceData?.general_information?.[
                    "policy_holder_street_address"
                  ]
                }
                onChange={(e) => onChangeGenreralInfo(e)}
                className={
                  invoiceData?.general_information?.[
                    "policy_holder_street_address"
                  ]?.trim() === "" ||
                  invoiceData?.general_information?.[
                    "policy_holder_street_address"
                  ] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                placeholder={t("policyholderAddress")}
                disabled={!isOwner}
              />
            </div>
            <div className="col-md-4 col-12">
              <div className="row">
                <div className="col-md-5 col-12">
                  <label className="time-gray fs12 poppins-medium mb5 text-nowrap">
                    {t("postalCode")}
                  </label>
                  <Input
                    name={"policy_holder_zipcode"}
                    value={
                      invoiceData?.general_information?.["policy_holder_zipcode"]
                    }
                    onChange={(e) => {
                      onChangeGenreralInfo(e);
                      validatePlz("policy_holder_zipcode", e.target.value);
                    }}
                    status={plzErrors.policy_holder_zipcode ? "error" : ""}
                    className={
                      invoiceData?.general_information?.[
                        "policy_holder_zipcode"
                      ]?.trim() === "" ||
                      invoiceData?.general_information?.[
                        "policy_holder_zipcode"
                      ] === null
                        ? "bg-invoiceorange"
                        : ""
                    }
                    placeholder={t("postalCode")}
                    disabled={!isOwner}
                  />{" "}
                  {plzErrors.policy_holder_zipcode && (
                    <div className="fs12 total-red mt5">{t("invalidPostalCode")}</div>
                  )}
                </div>
                <div className="col-md-7 col-12">
                  <label className="time-gray fs12 poppins-medium mb5">{t("city")}</label>
                  <Input
                    name={"policy_holder_city"}
                    value={invoiceData?.general_information?.["policy_holder_city"]}
                    onChange={(e) => onChangeGenreralInfo(e)}
                    className={
                      invoiceData?.general_information?.[
                        "policy_holder_city"
                      ]?.trim() === "" ||
                      invoiceData?.general_information?.["policy_holder_city"] ===
                        null
                        ? "bg-invoiceorange"
                        : ""
                    }
                    placeholder={t("city")}
                    disabled={!isOwner}
                  />{" "}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    },
  ];
  const calculate_discount = [
    {
      key: "4",
      label: (
        <div className="row">
          <div className="col-md-7 col-6 mx-md-0">
            <span className="fs18 poppins-medium dark-text">
              {t("totalSum")}:
            </span>
          </div>
          <div className="col-md-5 col-6 text-end my-auto pe-2 add-item-btn">
            <span
              className={
                verifyTotal()
                  ? `fs18 poppins-semibold total-green`
                  : "fs18 poppins-semibold total-red"
              }
            >
              {enableQuantityTotals
                ? manualOverrides.calculated_total_sum_after_discount
                  ? calculateManualSum()
                  : formatMoney(computeSubtotalAfterDiscount())
                : !isSumManual
                ? calculateAutoSumAfterDiscount()
                : calculateManualSum()}
            </span>
          </div>
        </div>
      ),
      children: (
        <div>
          <ul className="list-group text-gray-e pe-0 py-2">
            <li className="list-group-item row d-flex text-gray-e border-0">
              <div className="col-6">
                <div className="fs14 text-gray-e my-sm-2 my-0">
                  {t("InvoiceTotalSumPriceWithoutVAT")}:
                </div>
              </div>
              <div className="col-6 text-end pr45">
                <MoneyInput
                  plain
                  className="invoice-input"
                  name="invoice_total_sum_price_without_vat"
                  data-preview-field="invoice_total_sum_price_without_vat"
                  value={sumValues?.invoice_total_sum_price_without_vat}
                  onChange={(e) => handleOnChangeSumValues(e)}
                  disabled={!isOwner}
                />
              </div>
            </li>
            <li className="list-group-item row d-flex border-0">
              <div className="col-6">
                <div className="fs14 text-gray-e py-sm-2 py-0">
                  {t("InvoiceTotalSumPriceWithVAT")}:
                </div>
              </div>
              <div className="col-6 text-end pr45">
                <MoneyInput
                  plain
                  className="invoice-input"
                  name="invoice_total_sum_price_with_vat"
                  data-preview-field="invoice_total_sum_price_with_vat"
                  value={sumValues?.invoice_total_sum_price_with_vat}
                  onChange={(e) => handleOnChangeSumValues(e)}
                  disabled={!isOwner}
                />
              </div>
            </li>
            {!enableQuantityTotals && (
              <>
                <li className="list-group-item row d-flex border-0">
                  <div className="col-xl-6 col-md-4">
                    <div className="fs14 text-gray-e py-2">
                      {t("CalculatedTotalSum")}:
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 text-end pe-0">
                    <input
                      className="invoice-input"
                      name="calculated_total_sum"
                      value={calculateTotalSum()}
                      disabled
                    />
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 d-flex d-md-block text-end align-items-center">
                    <MoneyInput
                      plain
                      className="invoice-input"
                      name="calculated_total_sum_manual"
                      data-preview-field="calculated_total_sum_manual"
                      value={sumValues?.calculated_total_sum_manual}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!(isOwner && isSumManual)}
                    />
                    <Tooltip title={t("overrideAuto")} placement="bottom">
                      <Switch
                        size="small"
                        className="ml10"
                        checked={isSumManual}
                        onChange={handleSwitchManualSum}
                        disabled={!isOwner}
                      />
                    </Tooltip>
                  </div>
                </li>
                <li className="list-group-item row d-flex border-0">
                  <div className="col-lg-6 col-md-6">
                    <div className="fs14 text-gray-e py-2">
                      {t("TotalDiscountAmount")}:
                    </div>
                  </div>
                  <div className="col-md-6 col-8 text-end pr45">
                    <MoneyInput
                      plain
                      className="invoice-input discount-invoice-input"
                      name="total_discount_amount"
                      data-preview-field="total_discount_amount"
                      value={sumValues?.total_discount_amount}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!isOwner}
                    />
                  </div>
                </li>
                <li className="list-group-item row d-flex border-0">
                  <div className="col-xl-6 col-md-4 col-12">
                    <div className="fs14 text-gray-e py-2 poppins-semibold text-lg-nowrap">
                      {t("CalculatedTotalSumAfterDiscount")}:
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 text-end pe-0">
                    <input
                      className="invoice-input discount-invoice-input poppins-semibold"
                      name="calculated_total_sum_after_discount"
                      type="number"
                      value={(
                        (calculateTotalSum() - Math.abs((parseFloat(sumValues?.total_discount_amount) || 0)) || 0)
                      ).toFixed(2)}
                      disabled
                    />
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 d-flex d-md-block text-end align-items-center">
                    <MoneyInput
                      plain
                      className="invoice-input discount-invoice-input poppins-semibold"
                      name="calculated_total_sum_after_discount_manual"
                      data-preview-field="calculated_total_sum_after_discount_manual"
                      value={sumValues?.calculated_total_sum_after_discount_manual}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!(isOwner && isSumManual)}
                    />
                    <Tooltip title={t("overrideAuto")} placement="bottom">
                      <Switch
                        size="small"
                        className="ml10"
                        checked={isSumManual}
                        onChange={handleSwitchManualSum}
                        disabled={!isOwner}
                      />
                    </Tooltip>
                  </div>
                </li>
              </>
            )}
          </ul>
          {/* VAT breakdown is independent of enableQuantityTotals — it groups Item Price by rate. */}
          <VatBreakdown invoiceData={invoiceData} />
          {enableQuantityTotals && (
            <div className="qt-panel">
              <ul className="list-group text-gray-e pe-0 py-2">
                <li className="list-group-item row d-flex border-0">
                  <div className="col-xl-6 col-md-4">
                    <div className="fs14 text-gray-e py-2">
                      {t("CalculatedTotalSum")}:
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 text-end pe-0">
                    <input
                      className="invoice-input"
                      name="calculated_total_sum"
                      value={formatMoney(computeSubtotal())}
                      disabled
                    />
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 d-flex d-md-block text-end align-items-center">
                    <MoneyInput
                      plain
                      className="invoice-input"
                      name="calculated_total_sum_manual"
                      data-preview-field="calculated_total_sum_manual"
                      value={sumValues?.calculated_total_sum_manual}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!(isOwner && manualOverrides.calculated_total_sum)}
                    />
                    <Tooltip title={t("overrideAuto")} placement="bottom">
                      <Switch
                        size="small"
                        className="ml10"
                        checked={manualOverrides.calculated_total_sum}
                        onChange={() => handleSwitchOverride("calculated_total_sum")}
                        disabled={!isOwner}
                      />
                    </Tooltip>
                  </div>
                </li>
                <li className="list-group-item row d-flex border-0">
                  <div className="col-lg-6 col-md-6">
                    <div className="fs14 text-gray-e py-2">
                      {t("TotalDiscountAmount")}:
                    </div>
                    <div className="fs12 time-gray">
                      {t("discountAppliedAfterSubtotal")}
                    </div>
                  </div>
                  <div className="col-md-6 col-8 text-end pr45">
                    <MoneyInput
                      plain
                      className="invoice-input discount-invoice-input"
                      name="total_discount_amount"
                      data-preview-field="total_discount_amount"
                      value={sumValues?.total_discount_amount}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!isOwner}
                    />
                  </div>
                </li>
                <li className="list-group-item row d-flex border-0">
                  <div className="col-xl-6 col-md-4 col-12">
                    <div className="fs14 text-gray-e py-2 poppins-semibold text-lg-nowrap">
                      {t("CalculatedTotalSumAfterDiscount")}:
                    </div>
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 text-end pe-0">
                    <input
                      className="invoice-input discount-invoice-input poppins-semibold"
                      name="calculated_total_sum_after_discount"
                      value={formatMoney(computeSubtotalAfterDiscount())}
                      disabled
                    />
                  </div>
                  <div className="col-xl-3 col-md-4 col-6 col-sm-6 d-flex d-md-block text-end align-items-center">
                    <MoneyInput
                      plain
                      className="invoice-input discount-invoice-input poppins-semibold"
                      name="calculated_total_sum_after_discount_manual"
                      data-preview-field="calculated_total_sum_after_discount_manual"
                      value={sumValues?.calculated_total_sum_after_discount_manual}
                      onChange={(e) => handleOnChangeSumValues(e)}
                      disabled={!(isOwner && manualOverrides.calculated_total_sum_after_discount)}
                    />
                    <Tooltip title={t("overrideAuto")} placement="bottom">
                      <Switch
                        size="small"
                        className="ml10"
                        checked={manualOverrides.calculated_total_sum_after_discount}
                        onChange={() => handleSwitchOverride("calculated_total_sum_after_discount")}
                        disabled={!isOwner}
                      />
                    </Tooltip>
                  </div>
                </li>
              </ul>
              <div className="qt-compare pe-3 pb-2 text-end">
                {t("verificationCompare", {
                  legacy: formatMoney(parseFloat(calculateTotalSum()) || 0),
                  quantityAware: formatMoney(computeSubtotal()),
                })}
              </div>
            </div>
          )}
          <div
            className="d-flex justify-content-end align-items-center pe-3 pb-2"
            style={{ fontSize: 13 }}
          >
            {(() => {
              const calculated = enableQuantityTotals
                ? manualOverrides.calculated_total_sum_after_discount
                  ? parseFloat(calculateManualSum())
                  : computeSubtotalAfterDiscount()
                : !isSumManual
                ? calculateAutoSumAfterDiscount()
                : parseFloat(calculateManualSum());
              const calcNum = parseFloat(calculated) || 0;
              const invoiceTotal =
                parseFloat(sumValues?.invoice_total_sum_price_with_vat) ||
                parseFloat(sumValues?.invoice_total_sum_price_without_vat) ||
                0;
              const diff = calcNum - invoiceTotal;
              return verifyTotal() ? (
                <span className="total-green poppins-medium">
                  {t("totalsMatch", {
                    calculated: calcNum.toFixed(2),
                    total: invoiceTotal.toFixed(2),
                  })}
                </span>
              ) : (
                <span className="total-red poppins-medium">
                  {t("totalsDelta", {
                    calculated: calcNum.toFixed(2),
                    total: invoiceTotal.toFixed(2),
                    diff: diff.toFixed(2),
                  })}
                </span>
              );
            })()}
          </div>
        </div>
      ),
    },
  ];

  const treatments = invoiceData?.extracted_invoice_data[
    selectedPage - 1
  ]?.treatments?.map((item, index) => {
    return {
      key: item.key,
      label: (
        <div className="d-flex justify-content-between">
          <div className="d-flex align-items-center">
            <span className="dark-text fs18 poppins-medium d-inline-block pl25">
              {t("treatment")} <span className="ms-1 poppins-medium">{index + 1}</span>
            </span>
            <span className="dotgray ms-2">{item.Items?.length}</span>
          </div>
          <div className="">
            <Tooltip title={t("deleteTreatment")} placement="bottom">
              <button
                type="button"
                className="circle-btn"
                disabled={!isOwner}
                aria-label={t("deleteTreatment")}
                onClick={() => {
                  handleConfirmationPopup("treatment", item.key);
                }}
              >
                <img
                  src={trash}
                  alt=""
                  className="dotgray rounded-circle pt7 pb7 pl9 pr9"
                />
              </button>
            </Tooltip>
          </div>
        </div>
      ),
      children: (
        <div>
          <div className="row mt15 treatment-collapse">
            <div className="col-md-4 invoice-date">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("petName")}
              </label>
              <Input
                name="Pet Name"
                value={item["Pet Name"]}
                placeholder={t("petName")}
                data-preview-field="Pet Name"
                data-preview-treatment-key={item.key}
                onChange={(e) =>
                  updateTreatmentContent(
                    item.key,
                    null,
                    e.target.name,
                    e.target.value
                  )
                }
                className={
                  item["Pet Name"]?.trim() === "" || item["Pet Name"] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                disabled={!isOwner}
              />{" "}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("treatmentDate")}
              </label>
              <DateField
                name="Treatment Date"
                value={item["Treatment Date"]}
                placeholder={t("treatmentDate")}
                onChange={(e) =>
                  updateTreatmentContent(
                    item.key,
                    null,
                    e.target.name,
                    e.target.value
                  )
                }
                className={
                  item["Treatment Date"]?.trim() === "" ||
                  item["Treatment Date"] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                disabled={!isOwner}
              />{" "}
            </div>
            <div className="col-md-4 col-12">
              <label className="time-gray fs12 poppins-medium mb5">
                {t("diagnosis")}
              </label>
              <Input
                name="Diagnose"
                value={item["Diagnose"]}
                placeholder={t("diagnosis")}
                data-preview-field="Diagnose"
                data-preview-treatment-key={item.key}
                onChange={(e) =>
                  updateTreatmentContent(
                    item.key,
                    null,
                    e.target.name,
                    e.target.value
                  )
                }
                className={
                  item["Diagnose"]?.trim() === "" || item["Diagnose"] === null
                    ? "bg-invoiceorange"
                    : ""
                }
                disabled={!isOwner}
              />
            </div>
          </div>
          <div className="mt15 treatment-collapse-details">
            <div className="row d-none d-md-flex">
              <div className="col-md-6">
                <span className="time-gray fs12 poppins-medium mb5 d-inline-block pl25">
                  {t("itemName")}
                </span>
              </div>
              <div className="col-md-6">
                <div className="row">
                 <div className="col-md-11">
                   <div className="row">
                     <div className="col-md-4">
                      <span className="time-gray fs12 poppins-medium mb5 d-inline-block">{t("quantity")}</span>
                     </div>
                     <div className="col-md-4">
                      <span className="time-gray fs12 poppins-medium mb5 d-inline-block">{t("prize")}</span>
                     </div>
                     <div className="col-md-4">
                      <span className="time-gray fs12 poppins-medium mb5 d-inline-block">{t("VAT")}</span>
                     </div>
                   </div>
                  </div>
                 <div className="col-md-1">
                 </div>
                </div>
               </div>
            </div>

            <LineItem
              treatments={item}
              treatmentId={item.key}
              handleConfirmationPopup={handleConfirmationPopup}
              handleOnChangeItem={updateTreatmentContent}
              onReorderItems={reorderItemsInTreatment}
              onMoveItemToTreatment={moveItemToTreatment}
              onDuplicateItem={duplicateItemInTreatment}
              onMoveItemUpDown={moveItemUpDown}
              onItemHighlightStart={handleItemHighlightStart}
              onItemHighlightEnd={handleItemHighlightEnd}
              highlightedItemKey={previewHighlight?.itemKey}
              highlightedField={activePreviewField?.field}
              isEdit={!isOwner}
              selectedPage={selectedPage}
              treatmentIndex= {index}
              extracted_invoice_data = {invoiceData.extracted_invoice_data}
              classificationMetadata={classificationMetadata}
              classificationErrors={classificationErrors}
              feedbackStatuses={feedbackStatuses}
            />
          </div>
          <div className="row mt15 mb10">
            <div className="col-sm-6 col-4">
              <button
                onClick={() => addItemToTreatment(item.key)}
                className="fill-primary"
                disabled={!isOwner}
              >
                {t("addItem")}
              </button>
            </div>
            <div className="col-sm-6 col-8">
              <div className="outline-primary text-blue-a bg-transparent pe-none pl10 pr10 d-flex align-items-center justify-content-between">
                <div className="poppins-medium">{t("subTotal")}:</div>
                <div className="poppins-medium">
                  {calculateSubTotal(item)}
                </div>
              </div>
            </div>
          </div>
        </div>
      ),
    };
  });

  return (
    <>
      <Spin spinning={loading} size="large">
        <div>
          <div className="row flex-wrap-reverse mx-0 invoice-workspace">
            <div className="col-lg-7 pt30 pl30 pe-0 bg-white invoice-view" ref={invoiceFormRef}>
               <button
                  type="button"
                  onClick={handleOnBack}
                  className="handle-add-treatment-btn circle-btn invoice-back-btn"
                >
                  <img
                    src={backarrow}
                    alt=""
                    className="me-2 back-arrow"
                  />
                  {t("back")}
                </button>
              <div className="col-md-12 d-flex align-items-center page-footer mar-b-0 pad-l-0 bg-white justify-content-between flex-wrap flex-md-nowrap pr30 mb20">
                <span className="dark-text fs26 poppins-semibold">
                  {t("details")}
                </span>
                <div className="d-flex align-items-center flex-wrap flex-md-nowrap gap10">
                  <Tooltip title={t("addTreatment")} placement="bottom">
                    <button disabled={!isOwner}
                    className="circle-btn ml10" onClick={addTreatment}><img
                      src={addTreatmentIcon}
                      alt={t("addTreatment")}
                    /></button>
                  </Tooltip>
                  <Tooltip title={t("notes")} placement="bottom">
                    <button
                      type="button"
                      className="circle-btn ml10"
                      onClick={openNotesModel}
                      aria-label={t("notes")}
                    >
                      <img
                        src={notes ? InvoicenotesCompleted : Invoicenotes}
                        alt=""
                        className="cursor-pointer"
                      />
                    </button>
                  </Tooltip>
                  <Tooltip title={t("download")} placement="bottom">
                    <button
                      type="button"
                      className="circle-btn ml10"
                      onClick={handleExcelDownload}
                      disabled={!isOwner}
                      aria-label={t("download")}
                    >
                      <img src={Invoiceedit} alt="" />
                    </button>
                  </Tooltip>
                </div>
              </div>
              {!isOwner && invoiceData && (
                <Alert
                  type="info"
                  showIcon
                  className="mr30 mb20"
                  message={
                    invoiceData.assigned_user
                      ? t("readOnlyAssignedTo", { name: invoiceData.assigned_user })
                      : t("readOnlyNotAssigned")
                  }
                  action={
                    <Button
                      size="small"
                      type="primary"
                      onClick={handleOnClickAssign}
                    >
                      {invoiceData.assigned_user ? t("takeOver") : t("startProcessing")}
                    </Button>
                  }
                />
              )}
              {Object.keys(classificationErrors).length > 0 && (
                <Alert
                  type="error"
                  showIcon
                  role="alert"
                  aria-live="assertive"
                  className="mr30 mb20"
                  message={t("feedbackValidationTitle")}
                  description={t("feedbackValidationDescription")}
                />
              )}
              <div className="parent-div-invoice">
                <div className="my-0 collapse-invoice pr30 pad-r-15">
                  <Collapse
                    defaultActiveKey={["1"]}
                    items={general_information}
                    collapsible="header"
                    expandIconPosition="end"
                    expandIcon={({ isActive }) => (
                      <img src={isActive ? uparrow : downarrow} alt="" />
                    )}
                  />
                </div>
                <DragDropContext onDragEnd={onDragEnd}>
                  <Droppable droppableId="droppable" type="treatment">
                    {(provided) => (
                      <div ref={provided.innerRef} className="treatement-table pr30">
                        {treatments &&
                          treatments?.map((item, index) => (
                            <Draggable
                              key={String(item.key)}
                              draggableId={String(item.key)}
                              index={index}
                              isDragDisabled={!isOwner}
                            >
                              {(provided, snapshot) => (
                                <div
                                  ref={provided.innerRef}
                                  {...provided.draggableProps}
                                  style={getItemStyle(
                                    snapshot.isDragging,
                                    provided.draggableProps.style
                                  )}
                                >
                                  <div className="position-relative">
                                    <span className="drag-icon" {...provided.dragHandleProps}>
                                      <img
                                        src={drag}
                                        alt={t("drag")}
                                        className=""
                                      />
                                    </span>
                                    <div className="collapse-item">
                                      <Collapse
                                        key={String(item.key)}
                                        activeKey={
                                          openTreatmentKeys.includes(
                                            String(item.key)
                                          )
                                            ? [String(item.key)]
                                            : []
                                        }
                                        onChange={(keys) => {
                                          const next = (Array.isArray(keys)
                                            ? keys
                                            : [keys]
                                          ).map(String);
                                          setOpenTreatmentKeys((prev) => {
                                            const without = prev.filter(
                                              (key) => key !== String(item.key)
                                            );
                                            return next.includes(String(item.key))
                                              ? [...without, String(item.key)]
                                              : without;
                                          });
                                        }}
                                        collapsible="header"
                                        items={[item]}
                                        expandIconPosition="end"
                                        expandIcon={({ isActive }) => (
                                          <img
                                            src={isActive ? uparrow : downarrow}
                                            alt=""
                                          />
                                        )}
                                      />
                                    </div>
                                  </div>
                                </div>
                              )}
                            </Draggable>
                          ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </DragDropContext>
              </div>
              <CustomModal
                visible={confirmationPopup.isShow}
                modalTitle={confirmationPopup.type}
                handleConfirm={handleOnDeleteConfirmation}
                handleCancel={handleOnDeleteCancel}
                assignedTo={confirmationPopup.assigned_to}
              />

              <div className="page-footer invoice-totals-footer pad-l-0">
                <div className="my-0 collapse-footer invoice-totals-collapse">
                  <Collapse
                    activeKey={totalsCollapseOpen ? ["4"] : []}
                    onChange={(keys) => {
                      const next = Array.isArray(keys) ? keys : [keys];
                      setTotalsCollapseOpen(next.map(String).includes("4"));
                    }}
                    items={calculate_discount}
                    collapsible="header"
                    expandIconPosition="end"
                    expandIcon={({ isActive }) => (
                      <img src={isActive ? uparrow : downarrow} alt="" />
                    )}
                  />
                </div>
              </div>
            </div>
            <div className="col-lg-5 bg-view px-0 invoice-preview-pane">
              <h1 className="fs16 poppins-medium text-gray-e mt35 mb15 ml35 mar-l-15 mar-t-20">
                {invoiceData?.file_name || t("documentName")}
              </h1>
              <div className="invoice-preview-viewer">
                {invoiceData && (
                  <DocumentViewer
                    documentId={documentId}
                    pdfUrl={
                      invoiceData?.sas_document_link || invoiceData?.document_link
                    }
                    imageList={images || []}
                    fileName={invoiceData?.file_name}
                    currentIndex={selectedPage}
                    onChange={handleOnChangePage}
                    highlight={previewHighlight}
                    hitTargets={previewHitTargets}
                    onTargetHover={handlePreviewTargetHover}
                    onTargetSelect={handlePreviewTargetSelect}
                  />
                )}
              </div>
            </div>
          </div>
        </div>

        <div className="invoice-sticky-bar" ref={stickyBarRef}>
          <div className="invoice-sticky-bar__status">
            <span className="invoice-sticky-bar__saved">
              {lastSavedAt
                ? t("savedAt", {
                    time: lastSavedAt.toLocaleTimeString(t("currentLanguage"), {
                      hour: "2-digit",
                      minute: "2-digit",
                    }),
                  })
                : isDirty
                ? t("unsavedChanges")
                : ""}
            </span>
            {attentionCount > 0 ? (
              <button
                type="button"
                className="attention-counter"
                onClick={jumpToNextAttentionField}
                title={t("jumpToNextAttention")}
                aria-keyshortcuts="Alt+N"
              >
                {attentionCount} {t("fieldsNeedAttention")} (Alt+N)
              </button>
            ) : (
              <span className="attention-counter attention-counter--ok">
                {t("allFieldsComplete")}
              </span>
            )}
          </div>
          <div className="invoice-sticky-bar__actions">
            {enableItemClassification ? (
              <Button
                ghost
                className="invoice-sticky-bar__ghost"
                onClick={handleTranslateReasonings}
                disabled={!isOwner || loading || translatingReasonings}
                loading={translatingReasonings}
              >
                {t("translateReasoningsTo", {
                  language: t(currentLanguage?.toLowerCase?.() || "french"),
                })}
              </Button>
            ) : null}
            <Button onClick={handleOnUpdate} disabled={!isOwner || loading}>
              {t("save")} (Ctrl+S)
            </Button>
            <Button
              type="primary"
              onClick={handleSaveAndNext}
              disabled={!isOwner || loading}
            >
              {t("saveAndNext")} (Ctrl+Enter)
            </Button>
            <Button
              type="primary"
              ghost
              className="invoice-sticky-bar__ghost"
              onClick={handleComplete}
              disabled={!isOwner || loading}
            >
              {t("complete")} (Ctrl+E)
            </Button>
            <Dropdown
              menu={{
                items: [
                  {
                    key: "completeDownload",
                    icon: <DownloadOutlined />,
                    label: t("completeAndDownload"),
                  },
                  {
                    key: "reset",
                    icon: <RollbackOutlined />,
                    label: t("undoMyEdits"),
                    disabled: !isOwner,
                  },
                  {
                    key: "resetInitial",
                    icon: <WarningOutlined />,
                    label: t("restoreOriginal"),
                    disabled: !isOwner,
                    danger: true,
                  },
                  { type: "divider" },
                  {
                    key: "assignToggle",
                    icon: <UserSwitchOutlined />,
                    label: isOwner ? t("revoke") : t("assign"),
                  },
                ],
                onClick: ({ key }) => {
                  if (key === "completeDownload") {
                    handleExcelDownload();
                  } else if (key === "reset") {
                    handleConfirmationPopup("reset", null, null);
                  } else if (key === "resetInitial") {
                    handleConfirmationPopup("resetToInitial", null, null);
                  } else if (key === "assignToggle") {
                    handleOnClickAssign();
                  }
                },
              }}
              trigger={["click"]}
              placement="topRight"
              overlayClassName="app-dropdown"
            >
              <Button ghost className="invoice-sticky-bar__ghost">
                {t("moreActions")} <MoreOutlined />
              </Button>
            </Dropdown>
          </div>
        </div>

        <NotesModal
          open={isNotesModalVisible}
          onClose={handleNotesModalCancel}
          documentId={documentId}
          notesLog={invoiceData?.notes_log || []}
          legacyNote={notes}
          onSaved={handleNotesSaved}
        />
      </Spin>
    </>
  );
};
export default Invoice;
